import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SearchInput, { SearchInputStyles } from "@insite/content-library/Widgets/Header/SearchInput";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Search from "@insite/mobius/Icons/Search";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { PositionStyle } from "@insite/mobius/Popover";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

type Props = OwnProps;

interface State {
    mobileSearchModalIsOpen: boolean;
}

export interface MobileSearchStyles {
    mobileSearchWrapper?: InjectableCss;
    mobileSearchButton?: ButtonPresentationProps;
    mobileSearchModal?: ModalPresentationProps;
    searchInputStyles?: SearchInputStyles;
}

export const mobileSearchStyles: MobileSearchStyles = {
    mobileSearchWrapper: {
        css: css`
            text-align: right;
        `,
    },
    mobileSearchButton: {
        size: 48,
        buttonType: "solid",
        color: "white",
        css: css`
            padding: 0;
        `,
    },
    searchInputStyles: {
        input: {
            cssOverrides: {
                formField: css`
                    width: 100%;
                `,
            },
        },
        popover: {
            wrapperProps: {
                css: css`
                    width: calc(100% - 45px);
                `,
            },
        },
        popoverContentBody: {
            as: "div",
            _height: "100%",
            css: css`
                box-shadow: none;
            `,
        },
        autocompleteWrapper: {
            css: css`
                display: flex;
                flex-direction: column;
            `,
        },
        autocompleteColumnWrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                width: 100%;
                padding: 0 50px 0 30px;
            `,
        },
        searchHistoryStyles: {
            wrapper: {
                css: css`
                    display: flex;
                    flex-direction: column;
                    width: 100%;
                    padding: 0 50px 15px 30px;
                `,
            },
        },
        autocompleteProductsStyles: {
            titleLink: {
                css: css`
                    width: 100%;
                `,
            },
            erpNumberText: {
                css: css`
                    width: 100%;
                    text-align: left;
                    margin: 5px 0 0 0;
                `,
            },
        },
    },
};

const styles = mobileSearchStyles;

class MobileSearch extends React.Component<Props, State> {
    mobileSearchInput = React.createRef<HTMLInputElement>();

    constructor(props: Props) {
        super(props);
        this.state = {
            mobileSearchModalIsOpen: false,
        };
    }

    getMobileSearchAutocompletePosition = (element: React.RefObject<HTMLUListElement>) => {
        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top = rect.top + rect.height;
            positionStyle.left = 0;
            positionStyle.width = `${document.documentElement.clientWidth}px`;
            positionStyle.height = `${document.documentElement.clientHeight - positionStyle.top}px`;
        }

        return positionStyle;
    };

    mobileSearchButtonClickHandler = () => {
        this.setState({ mobileSearchModalIsOpen: true }, () => {
            setTimeout(() => {
                if (this.mobileSearchInput?.current) {
                    this.mobileSearchInput.current.focus();
                    this.mobileSearchInput.current.click();
                }
            }, 250);
        });
    };

    mobileSearchModalCloseHandler = () => {
        this.setState({ mobileSearchModalIsOpen: false });
    };

    render() {
        const { id } = this.props;

        return (
            <>
                <StyledWrapper {...styles.mobileSearchWrapper}>
                    <Button {...styles.mobileSearchButton} onClick={this.mobileSearchButtonClickHandler}>
                        <ButtonIcon src={Search} />
                        <VisuallyHidden>{translate("Search")}</VisuallyHidden>
                    </Button>
                </StyledWrapper>
                <Modal
                    {...styles.mobileSearchModal}
                    isOpen={this.state.mobileSearchModalIsOpen}
                    closeOnEsc={true}
                    handleClose={this.mobileSearchModalCloseHandler}
                    headline={
                        <SearchInput
                            id={id}
                            inputRef={this.mobileSearchInput}
                            autocompletePositionFunction={this.getMobileSearchAutocompletePosition}
                            onBeforeGoToUrl={this.mobileSearchModalCloseHandler}
                            extendedStyles={styles.searchInputStyles}
                        />
                    }
                />
            </>
        );
    }
}

export const mobileSearch: WidgetModule = {
    component: MobileSearch,
    definition: {
        group: "Header",
        displayName: "Mobile Search",
        allowedContexts: ["Header"],
        icon: "magnifying-glass",
    },
};

export default MobileSearch;
